import {
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NotificationService } from '@app/core/services/notification.service';
import { BulkActionHandlerFactory } from '@app/shared/components/bulk-actions/bulk-action-handler-factory';
import { BulkActionAssertion } from '@app/shared/components/bulk-actions/models/bulk-action-assertion';
import { BulkActionDialogOutput } from '@app/shared/components/bulk-actions/models/bulk-action-dialog-output';
import { BulkActionProcessingResults } from '@app/shared/components/bulk-actions/models/bulk-action-dialog-result';
import { BulkActionHandler } from '@app/shared/components/bulk-actions/models/bulk-action-handler';
import { BulkActionOptions } from '@app/shared/components/bulk-actions/models/bulk-action-options';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { DynamicComponentFactory } from '@app/shared/services/dynamic-component-factory.service';
import { BootstrapSemanticVariation } from '@app/utils/bootstrap';
import { PortalError } from '@app/utils/error/portal-error';
import { sortBy } from '@app/utils/sort/sort-by';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'portal-bulk-action-dialog',
  templateUrl: './bulk-action-dialog.component.html',
  standalone: false,
})
export class BulkActionDialogComponent<T = any>
  extends DialogComponent<BulkActionOptions<T>, BulkActionDialogOutput>
  implements BulkActionOptions<T>, OnDestroy, OnInit
{
  @ViewChild('handlerHost', { read: ElementRef, static: true })
  handlerHost!: ElementRef;

  actionLabel: string = 'bulkActions.submit';
  assert?: (entries: T[]) => BulkActionAssertion;
  body!: ((entry: T) => string) | TemplateRef<T>;
  handler: BulkActionHandlerFactory<T>;
  entries!: T[];
  label!: (entry: T) => string;
  title!: string;
  variant: BootstrapSemanticVariation = 'primary';

  handlerComponent: ComponentRef<BulkActionHandler<T>>;
  result: BulkActionProcessingResults | null = null;

  configurationError: PortalError[] | null = null;
  invalid: boolean = false;
  loading: boolean = false;
  pending: boolean = false;
  processInParallel: boolean = true;

  constructor(
    private dynamicComponentFactory: DynamicComponentFactory,
    private notificationService: NotificationService,
    ngbActiveModal: NgbActiveModal
  ) {
    super(ngbActiveModal);
  }

  ngOnDestroy(): void {
    this.handlerComponent.destroy();
  }

  ngOnInit(): void {
    this.sortEntries();

    this.handlerComponent = this.handler(this.handlerHost.nativeElement);
    this.invalid = this.handlerComponent.instance.invalid != null;
    this.loading = this.handlerComponent.instance.loading != null;

    this.handlerComponent.instance.error?.pipe(untilDestroyed(this)).subscribe((error) => {
      setTimeout(() => {
        if (error != null) {
          this.configurationError = Array.isArray(error) ? error : [error];
        } else {
          this.configurationError = null;
        }
      }, 0);
    });

    this.handlerComponent.instance.invalid?.pipe(untilDestroyed(this)).subscribe((invalid) => {
      setTimeout(() => {
        this.invalid = invalid;
      }, 0);
    });

    this.handlerComponent.instance.loading?.pipe(untilDestroyed(this)).subscribe((loading) => {
      setTimeout(() => {
        this.loading = loading;
      }, 0);
    });

    if (this.handlerComponent.instance.processInParallel != null) {
      this.processInParallel = this.handlerComponent.instance.processInParallel;
    }

    if (this.handlerComponent.instance.setOptions != null) {
      this.handlerComponent.instance.setOptions({
        actionLabel: this.actionLabel,
        assert: this.assert,
        body: this.body,
        entries: this.entries,
        handler: this.handler,
        label: this.label,
        title: this.title,
        variant: this.variant,
      });
    }
  }

  /**
   * Also called for dismiss because we need to emit the results.
   */
  close(): void {
    super.close({
      result: this.result,
    });
  }

  isTemplateRef(o: any): o is TemplateRef<any> {
    return o != null && typeof o !== 'string' && 'createEmbeddedView' in o;
  }

  onDismissed(reason?: any): void {
    this.emitClose({
      result: this.result,
    });
  }

  onSubmitting(): void {
    this.pending = true;

    if (this.handlerComponent.instance.disable != null) {
      this.handlerComponent.instance.disable();
    }
  }

  onSubmitted(result: BulkActionProcessingResults): void {
    this.pending = false;
    this.result = result;

    if (result.allSucceeded) {
      this.notificationService.showSuccess('bulkActions.result.allSucceeded');
    } else if (result.allFailed) {
      this.notificationService.showDanger('bulkActions.result.allFailed');
    } else {
      this.notificationService.showWarning('bulkActions.result.someFailed');
    }
  }

  private sortEntries(): void {
    this.entries = this.entries
      .map((entry) => {
        return {
          entry,
          label: this.label(entry).toLowerCase(),
        };
      })
      .sort(sortBy('label'))
      .map((_) => _.entry);
  }
}
